import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { merchantPayments } from '@/api/client';
import { Empty, ErrorBox } from '@/components/empty';
import { PaymentStatusBadge } from '@/components/status-badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatLyd } from '@/lib/utils';

export const Route = createFileRoute('/m/payments')({
  component: MerchantPayments,
});

function MerchantPayments() {
  const { t } = useTranslation();
  const list = useQuery({
    queryKey: ['merchant-payments-all'],
    queryFn: () => merchantPayments.list({ limit: 200 }),
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('payments.title')}</h1>
      </header>

      {list.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('payments.cols.created')}</TableHead>
              <TableHead>{t('payments.cols.order_ref')}</TableHead>
              <TableHead>{t('payments.cols.psp')}</TableHead>
              <TableHead>{t('payments.cols.amount')}</TableHead>
              <TableHead>{t('payments.cols.status')}</TableHead>
              <TableHead>{t('payments.cols.psp_ref')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-xs whitespace-nowrap">
                  {dayjs(p.created_at).format('MMM D HH:mm:ss')}
                </TableCell>
                <TableCell className="font-medium">
                  <Link
                    to="/m/payments/$id"
                    params={{ id: p.id }}
                    className="hover:underline"
                  >
                    {p.merchant_order_ref}
                  </Link>
                </TableCell>
                <TableCell className="capitalize">{p.psp_code}</TableCell>
                <TableCell className="tabular-nums">{formatLyd(p.amount_minor)}</TableCell>
                <TableCell>
                  <PaymentStatusBadge status={p.status} />
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {p.psp_ref ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!list.isLoading && list.data?.length === 0 && (
          <Empty title={t('payments.no_results')} className="border-0" />
        )}
      </div>
    </div>
  );
}
